/**
 * The shared screens on the voice channel page — one pane each, because
 * several people can present at the same time. One of them is the focus and
 * fills the stage; the rest sit as thumbnails underneath, and clicking a
 * thumbnail swaps it in. That is how "enter someone's share" works: you walk
 * into the channel, and pick whose screen you want to look at.
 *
 * Your own share is never played back: the capture would include this very
 * element, and the picture would tunnel into itself. A confirmation card says
 * what's happening instead, which is also all you need to know.
 */
import { Maximize2, Minimize2, MonitorOff, MonitorUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useVoiceRoom, type VoiceScreenShare } from '../../contexts/VoiceRoomContext';
import type { Track } from 'livekit-client';
import { LiveBadge } from './VoiceChannelRow';

function ScreenVideo({ track, className }: { track: Track; className: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Attach imperatively: the MediaStream belongs to the SDK, and React has no
  // prop for it. Detaching on cleanup is what stops the track being decoded
  // after the pane is gone. The same track can be attached to the big pane and
  // a thumbnail at once — the SDK fans one decode out to every element.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      // Screen audio arrives as a separate track and is played through the
      // room's audio elements; unmuting here would double it.
      muted
      className={className}
    />
  );
}

/**
 * Full screen is the whole pane, not the <video>: a fullscreened <video> gets
 * the browser's own chrome and loses our caption and button. Double-clicking
 * the picture toggles it too, the way every player does.
 */
function FocusedScreen({ share }: { share: VoiceScreenShare }) {
  const { t } = useI18n();
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === paneRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = () => {
    const el = paneRef.current;
    if (!el) return;
    // Best-effort: iOS Safari has no element fullscreen at all, and a refusal
    // (no user gesture, permissions policy) must not throw into React.
    if (document.fullscreenElement === el) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void el.requestFullscreen?.().catch(() => undefined);
    }
  };

  const label = fullscreen ? t('teamChat.voiceExitFullscreen') : t('teamChat.voiceFullscreen');
  return (
    <div
      ref={paneRef}
      className={[
        'relative overflow-hidden bg-black',
        fullscreen ? 'flex h-full w-full flex-col' : 'rounded-lg border border-border',
      ].join(' ')}
    >
      <div onDoubleClick={toggleFullscreen} className={fullscreen ? 'min-h-0 flex-1' : ''}>
        <ScreenVideo
          track={share.track}
          className={
            fullscreen
              ? 'h-full w-full object-contain'
              : 'mx-auto max-h-[70vh] w-full object-contain'
          }
        />
      </div>
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-white/70">
        <MonitorUp size={12} />
        <span className="min-w-0 flex-1 truncate">
          {t('teamChat.voiceSharingBy', { name: share.publisherName })}
        </span>
        {typeof document !== 'undefined' && document.fullscreenEnabled && (
          <button
            type="button"
            onClick={toggleFullscreen}
            title={label}
            aria-label={label}
            className="rounded p-1 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
          >
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

function ScreenThumbnail({
  share,
  active,
  onFocus,
}: {
  share: VoiceScreenShare;
  active: boolean;
  onFocus: () => void;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={onFocus}
      title={t('teamChat.voiceWatchOf', { name: share.publisherName })}
      aria-pressed={active}
      className={[
        'w-44 shrink-0 overflow-hidden rounded-md border bg-black text-left transition-colors',
        active ? 'border-emerald-500' : 'border-border hover:border-foreground/40',
      ].join(' ')}
    >
      <ScreenVideo track={share.track} className="aspect-video w-full object-contain" />
      <span className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-white/80">
        <LiveBadge />
        <span className="truncate">{share.publisherName}</span>
      </span>
    </button>
  );
}

export function LocalShareNotice() {
  const { t } = useI18n();
  const { toggleScreenShare } = useVoiceRoom();
  return (
    <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2">
      <MonitorUp size={14} className="shrink-0 text-emerald-600" />
      <span className="min-w-0 flex-1 truncate text-[12px] text-emerald-700">
        {t('teamChat.voiceSharingYours')}
      </span>
      <button
        type="button"
        onClick={() => void toggleScreenShare()}
        className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-[12px] text-emerald-700 hover:bg-emerald-500/15"
      >
        <MonitorOff size={13} />
        {t('teamChat.voiceStopSharing')}
      </button>
    </div>
  );
}

/**
 * Remote shares only — the caller filters ours out and shows LocalShareNotice.
 * `focusedId` is the publisher whose screen fills the stage; an id that no
 * longer has a share (they stopped) falls back to the first one, so the stage
 * never goes blank while someone is still presenting.
 */
export function ScreenShareStage({
  shares,
  focusedId,
  onFocus,
}: {
  shares: VoiceScreenShare[];
  focusedId: string | null;
  onFocus: (publisherId: string) => void;
}) {
  if (shares.length === 0) return null;
  const focused = shares.find((s) => s.publisherId === focusedId) ?? shares[0]!;
  return (
    <div className="space-y-2">
      <FocusedScreen share={focused} />
      {shares.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {shares.map((share) => (
            <ScreenThumbnail
              key={share.publisherId}
              share={share}
              active={share.publisherId === focused.publisherId}
              onFocus={() => onFocus(share.publisherId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
